import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronUp, Loader2, RotateCw } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import type { WriterVersionSummary } from '@/components/writer/WriterVersionScrubber';
import {
  parseCompileLog,
  type CompileDiagnostic,
} from '@/lib/writer/compileDiagnostics';
import { buildRepairHandoff, type HandoffCta } from '@/lib/writer/repairHandoff';
import { dispatchWriterPrefillPrompt } from '@/lib/writer/deepLink';

interface Props {
  latest: WriterVersionSummary | null;
  busy?: boolean;
  onRetry?: (v: WriterVersionSummary) => void | Promise<void>;
}

/**
 * Persistent banner shown above the compiled PDF pane when the latest
 * version failed to compile. Renders the Layer 2 templated handoff
 * (headline + cause + CTAs) keyed on the top parsed diagnostic — zero
 * tokens per exhaustion message — with a "Show details" affordance that
 * expands to the full diagnostic list and a raw log excerpt as a
 * fallback for cases the parser can't classify.
 *
 * See `src/lib/writer/repairHandoff.ts` and .lovable/plan.md — "Layer 2
 * — Templated handoff (zero tokens)".
 */
const WriterCompileStatusBanner: React.FC<Props> = ({ latest, busy, onRetry }) => {
  const [errorText, setErrorText] = useState<string | null>(null);
  const [serverDiagnostics, setServerDiagnostics] = useState<CompileDiagnostic[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    if (!latest || latest.compile_status !== 'failed') {
      setErrorText(null);
      setServerDiagnostics(null);
      setShowDetails(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      const [{ data: version }, { data: job }] = await Promise.all([
        supabase
          .from('writer_versions')
          .select('compile_diagnostics')
          .eq('id', latest.id)
          .maybeSingle(),
        supabase
          .from('writer_compile_jobs')
          .select('error')
          .eq('version_id', latest.id)
          .order('enqueued_at', { ascending: false })
          .limit(1)
          .maybeSingle(),
      ]);
      if (cancelled) return;
      const diag = (version as { compile_diagnostics?: CompileDiagnostic[] | null } | null)
        ?.compile_diagnostics;
      setServerDiagnostics(Array.isArray(diag) && diag.length > 0 ? diag : null);
      setErrorText(
        ((job as { error?: string | null } | null)?.error ?? '').trim() || null,
      );
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [latest]);

  const diagnostics: CompileDiagnostic[] = useMemo(
    () => serverDiagnostics ?? parseCompileLog(errorText),
    [serverDiagnostics, errorText],
  );

  const handoff = useMemo(() => buildRepairHandoff(diagnostics), [diagnostics]);

  if (!latest || latest.compile_status !== 'failed') return null;

  const handleCta = (cta: HandoffCta) => {
    if (cta.id === 'ask_agent' && onRetry) {
      void onRetry(latest);
      return;
    }
    if (cta.id === 'show_log') {
      setShowDetails((v) => !v);
      return;
    }
    // Specific CTAs (add_citation / edit_section / add_package / …)
    // deep-link into the chat composer by dispatching a prefill event.
    // See `src/lib/writer/deepLink.ts` — semantics are prefill-only, the
    // author still hits Send to spend any tokens.
    if (cta.prompt) {
      dispatchWriterPrefillPrompt({ prompt: cta.prompt, source: cta.id });
      return;
    }
    setShowDetails(true);
  };


  return (
    <div
      role="alert"
      className="border-b border-destructive/40 bg-destructive/5 px-4 py-3"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-destructive">
            {handoff?.headline ??
              `Latest compile failed${latest.label ? ` — ${latest.label}` : ''}`}
          </p>
          {handoff?.cause && (
            <p className="mt-1 text-xs text-muted-foreground">{handoff.cause}</p>
          )}

          {loading ? (
            <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" /> Loading error details…
            </div>
          ) : (
            <div className="mt-2 flex flex-wrap gap-2">
              {(handoff?.ctas ?? []).map((cta) => {
                const isRetry = cta.id === 'ask_agent';
                const isDetails = cta.id === 'show_log';
                const disabled = isRetry && (busy || !onRetry);
                return (
                  <Button
                    key={cta.id}
                    size="sm"
                    variant={cta.primary ? 'default' : 'outline'}
                    onClick={() => handleCta(cta)}
                    disabled={disabled}
                    className="h-7 px-2.5 text-xs"
                  >
                    {isRetry && busy ? (
                      <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
                    ) : isRetry ? (
                      <RotateCw className="h-3 w-3 mr-1.5" />
                    ) : isDetails ? (
                      showDetails ? (
                        <ChevronUp className="h-3 w-3 mr-1.5" />
                      ) : (
                        <ChevronDown className="h-3 w-3 mr-1.5" />
                      )
                    ) : null}
                    {cta.label}
                  </Button>
                );
              })}
              {/* Fallback retry when no handoff exists (unparseable log). */}
              {!handoff && onRetry && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onRetry(latest)}
                  disabled={busy}
                  className="h-7 px-2.5 text-xs"
                >
                  {busy ? (
                    <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
                  ) : (
                    <RotateCw className="h-3 w-3 mr-1.5" />
                  )}
                  Retry compile
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      {showDetails && !loading && (
        <div className="mt-3 ml-7 border-t border-destructive/20 pt-3">
          {diagnostics.length > 0 ? (
            <ul className="space-y-1 text-xs">
              {diagnostics.slice(0, 12).map((d, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span
                    className={
                      d.severity === 'error'
                        ? 'text-destructive font-medium'
                        : 'text-amber-600 dark:text-amber-400 font-medium'
                    }
                  >
                    {d.severity === 'error' ? 'Error' : 'Warning'}
                  </span>
                  <span className="text-muted-foreground min-w-0 break-words">
                    {d.message}
                    {d.line ? (
                      <span className="opacity-60"> · line {d.line}</span>
                    ) : null}
                  </span>
                </li>
              ))}
              {diagnostics.length > 12 && (
                <li className="text-[11px] text-muted-foreground opacity-70">
                  +{diagnostics.length - 12} more…
                </li>
              )}
            </ul>
          ) : errorText ? (
            <pre className="text-xs text-muted-foreground whitespace-pre-wrap break-words max-h-48 overflow-y-auto font-mono">
              {errorText.slice(0, 2000)}
            </pre>
          ) : (
            <p className="text-xs text-muted-foreground">
              No error details recorded. Try re-queuing the compile.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default WriterCompileStatusBanner;
